import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DataGrid, GridToolbarContainer, useGridApiRef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import linguistApi, { waitForLinguistAuthUser } from "services/linguistApi";
import { useWebSocket } from "contexts/WebSocketContext";
import { auth } from "../../firebase/firebase";
import DataGridHeader from "components/DataGridHeader";
import ColumnDateFilter from "components/ColumnDateFilter";
import useColumnFilters from "hooks/useColumnFilters";
import { CONST } from "constants/CONST";
import CardDashboardText from "components/CardDashboardText";
import ColumnFilter from "components/ColumnFilter";
import { HtmlTooltip } from "components/HeatMap";
import ClearIcon from "@mui/icons-material/ClearRounded";
import CEQuickFilter from "components/CEQuickFilter";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import { useStateContext } from "contexts/ContextProvider";
import { loadExcelJS } from "helpers/loadExcelJS";
import { NAVBAR_HEIGHT } from "constants/layout";
import { getStatusColor, getDateProximityColor } from "helpers/ServiceHelpers";

const isInterpretationService = (item) => {
  if (String(item?.ServiceCategory || "").toLowerCase() === "interpretation") return true;
  return Boolean(item?.InterpretationStatus);
};

const getEstimatedIncome = (item) => {
  const fromTotalCost = Number(item?.TotalCost);
  if (Number.isFinite(fromTotalCost) && fromTotalCost > 0) return fromTotalCost;

  const fromLinguistPaid = Number(item?.LinguistPaidAmount);
  if (Number.isFinite(fromLinguistPaid) && fromLinguistPaid > 0) return fromLinguistPaid;

  const fromRate = Number(item?.Rate);
  if (Number.isFinite(fromRate) && fromRate > 0) return fromRate;

  return 0;
};

const formatServiceTime = (value) => {
  if (!value || value === "00:00:00") return "N/A";
  return dayjs.tz(value, "HH:mm:ss", "America/New_York").format("h:mm A");
};

const OFFER_STATUS_LABELS = {
  Offered: "Pending",
  Accepted: "Accepted",
  Declined: "Declined",
  Expired: "Expired",
  Withdrawn: "Withdrawn",
};

const getOfferDisplayStatus = (status) => {
  const normalizedStatus = String(status || "").trim();
  return OFFER_STATUS_LABELS[normalizedStatus] || normalizedStatus || "N/A";
};

const getOfferStatusColor = (theme, status) => {
  switch (String(status || "").trim()) {
    case "Offered":
      return "warning";
    case "Accepted":
      return "success";
    case "Declined":
      return "error";
    case "Expired":
      return "default";
    case "Withdrawn":
      return "info";
    default:
      return "default";
  }
};

const formatOfferDate = (value) => {
  if (!value) return "N/A";

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : String(value);
};

const formatOfferTime = (value) => {
  if (!value) return "N/A";

  const stringValue = String(value).trim();
  if (!stringValue) return "N/A";

  const directMatch = stringValue.match(
    /^(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*([AP]M))?$/i,
  );
  if (directMatch) {
    let hours = Number(directMatch[1]);
    const minutes = directMatch[2];
    const meridian = directMatch[3]?.toUpperCase() || "";

    if (meridian === "PM" && hours < 12) hours += 12;
    if (meridian === "AM" && hours === 12) hours = 0;

    return `${String(hours).padStart(2, "0")}:${minutes}`;
  }

  const parsed = dayjs(stringValue);
  return parsed.isValid() ? parsed.format("HH:mm") : stringValue;
};

const getOfferServiceType = (service) =>
  String(
    service?.ServiceSubtype?.Name ||
      service?.InterpretationType ||
      service?.ServiceType ||
      service?.Type ||
      service?.ServiceCategory ||
      service?.Mode ||
      "",
  ).trim() || "N/A";

const getOfferAddress = (service) =>
  String(
    service?.InPersonAddress ||
      service?.Address ||
      service?.LocationAddress ||
      service?.Location ||
      "",
  ).trim();

const getOfferSpecialInstructions = (service) =>
  String(
    service?.SpecialInstructions ||
      service?.SpecialInstruction ||
      service?.Instructions ||
      service?.Details ||
      "",
  ).trim();

const isInPersonOffer = (service) => {
  const locationMode = String(
    service?.Mode || service?.LocationType || service?.ServiceMode || "",
  )
    .trim()
    .toLowerCase();

  return Boolean(
    getOfferAddress(service) &&
      (!locationMode || /in[\s-]?person|on[\s-]?site|onsite/.test(locationMode)),
  );
};

const calculateLinguistPay = (startTime, endTime, hourlyRate) => {
  if (!startTime || !endTime || !hourlyRate) return "N/A";

  try {
    const startParts = String(startTime).match(
      /^(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*([AP]M))?$/i,
    );
    const endParts = String(endTime).match(
      /^(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*([AP]M))?$/i,
    );

    if (!startParts || !endParts) return "N/A";

    let startHour = Number(startParts[1]);
    const startMinute = Number(startParts[2]);
    const startMeridian = startParts[3]?.toUpperCase() || "";

    let endHour = Number(endParts[1]);
    const endMinute = Number(endParts[2]);
    const endMeridian = endParts[3]?.toUpperCase() || "";

    if (startMeridian === "PM" && startHour < 12) startHour += 12;
    if (startMeridian === "AM" && startHour === 12) startHour = 0;
    if (endMeridian === "PM" && endHour < 12) endHour += 12;
    if (endMeridian === "AM" && endHour === 12) endHour = 0;

    const durationHours = (endHour * 60 + endMinute - startHour * 60 - startMinute) / 60;

    if (durationHours <= 0) return "N/A";

    const pay = durationHours * Number(hourlyRate);
    return `$${pay.toFixed(2)}`;
  } catch {
    return "N/A";
  }
};

const InterpretationAssignmentsGrid = ({
  assignments = [],
  selectedYear,
  selectedMonths = [],
  onOpenAssignment,
  assignmentTab = "all",
  onAssignmentTabChange,
}) => {
  const theme = useTheme();

  const listOfServices = useMemo(
    () => assignments.filter(isInterpretationService),
    [assignments],
  );


  const now = dayjs();
  const startOfToday = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const needsFinalizationItems = useMemo(() => {
    return listOfServices.filter((item) => {
      const itemDate = dayjs(item.Date);
      return (
        itemDate.isBefore(startOfToday, "day") &&
        String(item.InterpretationStatus || "").trim() === "Confirmed"
      );
    });
  }, [listOfServices, startOfToday]);

  const needsFinalizationCount = needsFinalizationItems.length;

  const filteredByTab = useMemo(() => {
    if (assignmentTab === "finalization") {
      return needsFinalizationItems;
    }

    return listOfServices.filter(
      (item) => !needsFinalizationItems.some((finalizationItem) => finalizationItem.id === item.id),
    );
  }, [assignmentTab, listOfServices, needsFinalizationItems]);

  const [openFilter, setOpenFilter] = useState(null);
  const { showSnackbar, openDialog, closeDialog } = useStateContext();

  const selectedPeriodAssignments = useMemo(() => {
    const selectedMonthIndices = (selectedMonths || [])
      .map((monthName) => dayjs().month(monthName).month())
      .filter((value) => Number.isFinite(value) && value >= 0 && value <= 11);

    return listOfServices.filter((item) => {
      if (!item?.Date) return false;

      const itemDate = dayjs(item.Date);
      if (!itemDate.isValid()) return false;
      if (String(itemDate.year()) !== String(selectedYear)) return false;
      if (
        selectedMonthIndices.length > 0 &&
        !selectedMonthIndices.includes(itemDate.month())
      )
        return false;
      return true;
    });
  }, [listOfServices, selectedMonths, selectedYear]);

  const estimatedIncome = useMemo(
    () =>
      selectedPeriodAssignments.reduce(
        (sum, item) => sum + getEstimatedIncome(item),
        0,
      ),
    [selectedPeriodAssignments],
  );

  const todayAssignments = useMemo(() => {
    const todayKey = now.format("YYYY-MM-DD");
    return listOfServices.filter(
      (item) =>
        item?.Date && dayjs(item.Date).format("YYYY-MM-DD") === todayKey,
    );
  }, [listOfServices, now]);

  const tomorrowAssignments = useMemo(() => {
    const tomorrowKey = now.add(1, "day").format("YYYY-MM-DD");
    return listOfServices.filter(
      (item) =>
        item?.Date && dayjs(item.Date).format("YYYY-MM-DD") === tomorrowKey,
    );
  }, [listOfServices, now]);

  const thisWeekAssignments = useMemo(() => {
    const start = now.startOf("week");
    const end = now.endOf("week");
    return listOfServices.filter((item) => {
      if (!item?.Date) return false;
      const itemDate = dayjs(item.Date);
      return (
        itemDate.isValid() &&
        (itemDate.isAfter(start) || itemDate.isSame(start, "day")) &&
        (itemDate.isBefore(end) || itemDate.isSame(end, "day"))
      );
    });
  }, [listOfServices, now]);

  const nextWeekAssignments = useMemo(() => {
    const start = now.add(1, "week").startOf("week");
    const end = now.add(1, "week").endOf("week");
    return listOfServices.filter((item) => {
      if (!item?.Date) return false;
      const itemDate = dayjs(item.Date);
      return (
        itemDate.isValid() &&
        (itemDate.isAfter(start) || itemDate.isSame(start, "day")) &&
        (itemDate.isBefore(end) || itemDate.isSame(end, "day"))
      );
    });
  }, [listOfServices, now]);

  const getSavedColumnsVisible = () => {
    const saved = localStorage.getItem("INTERPRETATIONS_COLUMNS_VISIBLE");
    const parsed = saved ? JSON.parse(saved) : {};

    return {
      ...parsed,
      LinguistPay: true,
      TotalLinguistPay: true,
      AdditionalCost: true,
      MileageCost: true,
      ParkingFeesTolls: true,
      Address: true,
    };
  };

  const [columnsVisible] = useState(getSavedColumnsVisible);

  const getFilterStorageKey = useCallback((fieldName) => {
    const upperFieldName = fieldName.toUpperCase();
    return `INTERPRETATIONS_FILTER_${upperFieldName}`;
  }, []);

  const [, setOverlayMode] = useState(null);

  const saveColumnFilter = useCallback(
    (fieldName, values) => {
      const key = getFilterStorageKey(fieldName);
      if (values && values.length > 0) {
        localStorage.setItem(key, JSON.stringify(values));
      } else {
        localStorage.removeItem(key);
      }
    },
    [getFilterStorageKey],
  );

  const timeComparator = (v1, v2) => {
    const format = "h:mm A";
    const t1 = dayjs(v1, format);
    const t2 = dayjs(v2, format);

    return t1.isBefore(t2) ? -1 : t1.isAfter(t2) ? 1 : 0;
  };

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return dayjs(v1, "M/D/YYYY").valueOf() - dayjs(v2, "M/D/YYYY").valueOf();
  };

  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "ProjectNumber",
      "Date",
      "TimeStart",
      "TimeEnd",
      "Duration",
      "InterpretationStatus",
      "Address",
      "Assignment",
    ];

    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(CONST.INTERPRETATION_FILTER_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  };

  const getStoredSortModel = () => {
    const stored = localStorage.getItem(CONST.INTERPRETATION_SORT_KEY);
    return stored ? JSON.parse(stored) : [];
  };

  const getSavedDensity = () => {
    const saved = localStorage.getItem("INTERPRETATIONS_GRID_DENSITY");
    return saved || "standard";
  };

  const apiRef = useGridApiRef();

  const [, setOverlayOpen] = useState(false);
  const [sortModel, setSortModel] = useState(getStoredSortModel);
  const prevFilteredRowIdsRef = useRef([]);
  const [filterModel, setFilterModel] = useState(getStoredFilterModel);
  const [filterRowIds, setFilterRowIds] = useState([]);
  const [density] = useState(getSavedDensity);

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    localStorage.setItem(CONST.INTERPRETATION_SORT_KEY, JSON.stringify(newSortModel));
  };

  const columns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        width: 140,
        renderHeader: () => <DataGridHeader headerName="Project #" />,
      },
      {
        field: "Date",
        headerName: "Date",
        valueGetter: (_, params) => {
          const data = params.Date;
          const value =
            data && data !== "" ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
          return value;
        },
        sortComparator: dateComparator,
        minWidth: 100,
        flex: 1.0,
        renderCell: (params) => (
          <Box
            style={{ display: "flex", alignItems: "center", height: "100%" }}
          >
            <Typography
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: getDateProximityColor(
                  theme,
                  params.value,
                  params.row.InterpretationStatus,
                ),
              }}
            >
              {params.value}
            </Typography>
          </Box>
        ),
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnDateFilter
              data={filterData[1] || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "TimeStart",
        sortComparator: timeComparator,
        headerName: "Time Start",
        valueGetter: (_, params) => {
          const data = params.TimeStart;
          if (data === "00:00:00") {
            return "12:00 AM";
          }
          if (data && data !== "") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
        minWidth: 125,
        flex: 1.25,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={filterData[4] || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "TimeEnd",
        sortComparator: timeComparator,
        headerName: "Time End",
        valueGetter: (_, params) => {
          const data = params.TimeEnd;
          if (data === "00:00:00") {
            return "12:00 AM";
          }
          if (data && data !== "") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
        minWidth: 120,
        flex: 1.2,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={filterData[5] || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "Duration",
        headerName: "Duration",
        valueGetter: (_, params) => {
          const data = params.Duration;
          let value = "N/A";

          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            value = `${hours}:${minutes}`;
          }

          return value;
        },
        minWidth: 115,
        flex: 1.15,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "InterpretationStatus",
        headerName: "Status",
        minWidth: 120,
        flex: 1.2,
        renderCell: (params) => (
          <Box
            style={{ display: "flex", alignItems: "center", height: "100%" }}
          >
            <HtmlTooltip
              title={
                <Typography sx={{ whiteSpace: "pre-wrap" }}>
                  {params.value}
                </Typography>
              }
            >
              <Box
                sx={{
                  borderRadius: "8px",
                  px: "0.5rem",
                  backgroundColor: getStatusColor(theme, params.value),
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: theme.palette.background.default,
                  }}
                >
                  {params.value}
                </Typography>
              </Box>
            </HtmlTooltip>
          </Box>
        ),
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "Address",
        headerName: "Address",
        valueGetter: (_, params) => {
          const data = params.Address;
          const value = data && data !== "" ? data : "N/A";
          return value;
        },
        renderCell: (params) => (
          <Box
            style={{ display: "flex", alignItems: "center", height: "100%" }}
          >
            <Typography
              sx={{
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }}
              style={{ whiteSpace: "pre-wrap" }}
            >
              {params.value}
            </Typography>
          </Box>
        ),
        minWidth: 220,
        flex: 2.2,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "Name",
        minWidth: 220,
        flex: 1,
        renderHeader: () => <DataGridHeader headerName="Assignment" />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- renderHeader closures read columnFilters/filterData/handleColumnFilterChangeWithStorage (declared after this memo - TDZ) + listOfServices/openFilter/sortModel/theme; deps kept stable so column identity persists (quick-filter focus)
    [onOpenAssignment],
  );

  const filteredColumns = columns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(filteredByTab || [], columns);

  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();
    setFilterModel({ items: [] });
    localStorage.setItem(
      CONST.INTERPRETATION_FILTER_KEY,
      JSON.stringify({ items: [] }),
    );
  }, [clearAllFilters, clearAllColumnFilters]);

  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (row) =>
          row.ProjectNumber && row.ProjectNumber !== ""
            ? row.ProjectNumber
            : "N/A",
      },
      {
        field: "Date",
        valueGetter: (row) =>
          row.Date && row.Date !== ""
            ? dayjs.tz(row.Date).format("M/D/YYYY")
            : "N/A",
      },
      {
        field: "TimeStart",
        valueGetter: (row) => {
          const data = row.TimeStart;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "TimeEnd",
        valueGetter: (row) => {
          const data = row.TimeEnd;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "Duration",
        valueGetter: (row) => {
          const data = row.Duration;
          if (data && data !== "00:00:00") {
            return `${data.split(":")[0]}:${data.split(":")[1]}`;
          }
          return "N/A";
        },
      },
      {
        field: "InterpretationStatus",
        valueGetter: (row) => row.InterpretationStatus || "N/A",
      },
      {
        field: "LinguistPay",
        valueGetter: (row) => {
          const data = row.LinguistPay;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "InterpretationType",
        valueGetter: (row) => row.ServiceSubtype?.Name || "N/A",
      },
    ],
    [],
  );

  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return filteredByTab || [];
      }

      return (filteredByTab || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;

          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;

          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, filteredByTab, filterColumns]);

  const handleColumnFilterChangeWithStorage = useCallback(
    (field, values) => {
      handleColumnFilterChange(field, values);
      saveColumnFilter(field, values);
    },
    [handleColumnFilterChange, saveColumnFilter],
  );

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    localStorage.setItem(CONST.INTERPRETATION_FILTER_KEY, JSON.stringify(newFilterModel));
  };

  const isMobile = !useMediaQuery("(min-width:600px)");

  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No interpretations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = filteredByTab.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : filteredByTab || [];

              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              const csvRows = dataToExport
                .filter((row) => {
                  if (!row || typeof row !== "object") {
                    console.warn("Skipping invalid row:", row);
                    return false;
                  }
                  return true;
                })
                .map((row, rowIndex) => {
                  try {
                    return visibleColumns
                      .map((col) => {
                        try {
                          let value = "";
                          if (col.field) {
                            const mappedPath = fieldPaths[col.field];
                            if (mappedPath) {
                              value = getFieldValue(row, mappedPath);
                            } else if (col.field === "TargetLanguage") {
                              const languages = row.ServiceTargetLanguages;
                              if (languages && Array.isArray(languages)) {
                                value = languages
                                  .map((tl) => tl.Language?.Name || "")
                                  .join(" | ");
                              }
                            } else {
                              value = getFieldValue(row, col.field);
                              if (value === "" && col.field in row) {
                                value = row[col.field];
                              }
                            }
                          }
                          const strValue = String(value || "").replace(/"/g, '""');
                          return `"${strValue}"`;
                        } catch (cellError) {
                          console.warn(
                            `Error processing cell at row ${rowIndex}, column ${col.field}:`,
                            cellError.message,
                          );
                          return '""';
                        }
                      })
                      .join(",");
                  } catch (rowError) {
                    console.warn(`Error processing row ${rowIndex}:`, rowError.message);
                    return "";
                  }
                })
                .filter((row) => row.length > 0);

              const csvContent = [csvHeader, ...csvRows].join("\n");

              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `interpretations-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={async () => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No interpretations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = filteredByTab.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : filteredByTab || [];

              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              showSnackbar("Preparing XLSX export…", theme, "info");

              const ExcelJS = await loadExcelJS();
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Interpretations");

              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              const columnWidths = new Array(visibleColumns.length).fill(15);

              dataToExport.forEach((row) => {
                if (!row || typeof row !== "object") return;

                const rowData = visibleColumns.map((col, colIndex) => {
                  try {
                    let value = "";
                    if (col.field) {
                      const mappedPath = fieldPaths[col.field];
                      if (mappedPath) {
                        value = getFieldValue(row, mappedPath);
                      } else if (col.field === "TargetLanguage") {
                        const languages = row.ServiceTargetLanguages;
                        if (languages && Array.isArray(languages)) {
                          value = languages
                            .map((tl) => tl.Language?.Name || "")
                            .join(" | ");
                        }
                      } else {
                        value = getFieldValue(row, col.field);
                        if (value === "" && col.field in row) {
                          value = row[col.field];
                        }
                      }
                    }
                    const strValue = String(value || "");
                    columnWidths[colIndex] = Math.max(
                      columnWidths[colIndex],
                      Math.min(strValue.length + 2, 50),
                    );
                    return strValue;
                  } catch (error) {
                    console.warn(`Error processing cell:`, error.message);
                    return "";
                  }
                });
                worksheet.addRow(rowData);
              });

              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });

              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `interpretations-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
              showSnackbar(
                "Couldn't prepare the XLSX export — check your connection and try again.",
                theme,
                "error",
              );
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  // Export dialog bridge — keep the dialog factory fresh at click time. The
  // export MenuItem lives inside a memoized toolbar, so it captures
  // <ExportDialogContent /> from the render where that memo last recomputed —
  // which can predate the data load (stale closure → the dialog says
  // "No data available to export"). This ref is reassigned every commit, so
  // the click always opens the latest render's dialog with current data.
  const openExportDialogRef = useRef(null);
  useEffect(() => {
    openExportDialogRef.current = () =>
      openDialog({
        title: "Export Data",
        theme,
        customContent: <ExportDialogContent />,
      });
  });

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters: toolbarHasFilters,
      onClear,
      theme: toolbarTheme,
      currentTab,
      onTabChange,
      needsFinalizationCount = 0,
    }) => {
      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {!isMobile && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {toolbarHasFilters && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.alt,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobile && toolbarHasFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tabs
            value={currentTab}
            onChange={onTabChange}
            sx={{
              fontSize: "0.875rem",
              display: "flex",
              alignItems: "center",
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .MuiTab-root": {
                fontSize: "0.875rem",
                padding: "0px 12px",
                minHeight: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "12px",
                margin: "0 4px",
                color: theme.palette.text.primary,
                transition: "all 0.2s ease",
              },
              "& .MuiTab-root.Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.default,
                fontWeight: 700,
              },
            }}
          >
            <Tab label="All Assignments" value="all" />
            <Tab
              value="finalization"
              label={
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Box component="span">Needs Finalization:</Box>
                  <Box
                    component="span"
                    sx={{
                      color:
                        needsFinalizationCount > 0
                          ? toolbarTheme.palette.error.main
                          : "inherit",
                      fontWeight:
                        needsFinalizationCount > 0 ? 700 : 400,
                    }}
                  >
                    {needsFinalizationCount}
                  </Box>
                </Box>
              }
            />
          </Tabs>

          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <CEQuickFilter iconOnly={isMobile} />

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: toolbarTheme.palette.grey.main,
                  borderColor: toolbarTheme.palette.divider,
                  border: `1px solid ${toolbarTheme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: toolbarTheme.palette.primary.main,
                    color: toolbarTheme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setOverlayMode("density");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOverlayMode("columns");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  openExportDialogRef.current?.();
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [theme, isMobile]);

  const NoRowsOverlay = () => {
    const emptyMessage =
      assignmentTab === "finalization"
        ? "You have no assignments to finalize right now."
        : "No interpretation projects added yet for this month.";

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          px: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            textAlign: "center",
          }}
        >
          {emptyMessage}
        </Typography>
      </Box>
    );
  };

  return (
    <Grid
      container
      spacing={2}
      size={{ xs: 12 }}
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid
        container
        spacing={2}
        size={{ xs: 12 }}
        sx={{ px: 1, pt: 1, flexShrink: 0 }}
      >
        <Grid size={{ xs: 12, sm: 12 / 5 }}>
          <CardDashboardText
            title="Est. Income"
            content={`$${estimatedIncome.toFixed(2)}`}
          />
        </Grid>
        <Grid size={{ xs: 12/2, sm: 12 / 5 }}>
          <CardDashboardText
            title="Today"
            content={String(todayAssignments.length)}
          />
        </Grid>
        <Grid size={{ xs: 12/2, sm: 12 / 5 }}>
          <CardDashboardText
            title="Tomorrow"
            content={String(tomorrowAssignments.length)}
          />
        </Grid>
        <Grid size={{ xs: 12/2, sm: 12 / 5 }}>
          <CardDashboardText
            title="This Week"
            content={String(thisWeekAssignments.length)}
          />
        </Grid>
        <Grid size={{ xs: 12/2, sm: 12 / 5 }}>
          <CardDashboardText
            title="Next Week"
            content={String(nextWeekAssignments.length)}
          />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        size={{ xs: 12 }}
        sx={{ px: 1, pb: 1, minHeight: 0, flex: 1, minWidth: 0 }}
      >
        <Grid
          size={{ xs: 12 }}
          sx={{ display: "flex", minHeight: 0, flex: 1, minWidth: 0 }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              minWidth: 0,
            }}
          >
            {isMobile ? (
              <Box sx={{ width: "100%", overflowY: "auto", px: 0.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1 }}
                >
                  Assignments
                </Typography>
                {(columnFilteredData || []).map((row) => (
                  <Card
                    key={row.id}
                    sx={{ mb: 1, borderRadius: 2 }}
                    variant="outlined"
                  >
                    <CardActionArea
                      onClick={() => onOpenAssignment?.(row.id)}
                    >
                      <CardContent sx={{ pb: 1 }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1}
                        >
                          <Typography variant="subtitle2">
                            {row.ProjectNumber || `#${row.id}`}
                          </Typography>
                          <Typography variant="caption">
                            {row.Date
                              ? dayjs(row.Date).format("M/D/YYYY")
                              : "N/A"}
                          </Typography>
                        </Stack>

                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, mt: 0.5 }}
                        >
                          {row.Name || `Assignment #${row.id}`}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            width: "100%",
                            mt: 0.5,
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ flex: 1, textAlign: "left" }}>
                            <Typography variant="body2">
                              Start: {formatServiceTime(row.TimeStart)}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, textAlign: "center" }}>
                            <Typography variant="body2">
                              End: {formatServiceTime(row.TimeEnd)}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, textAlign: "right" }}>
                            <Typography variant="body2">
                              Duration: {row.Duration && row.Duration !== "00:00:00"
                                ? row.Duration.slice(0, 5)
                                : "N/A"}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ mt: 1 }}>
                          <Chip
                            size="small"
                            label={row.InterpretationStatus || "Assigned"}
                            sx={{
                              fontWeight: 700,
                              backgroundColor: getStatusColor(
                                theme,
                                row.InterpretationStatus,
                              ),
                              color: theme.palette.background.default,
                            }}
                          />
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                        >
                          {row.Address || "N/A"}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            ) : (
              <DataGrid
                localeText={{
                  noRowsLabel:
                    "No interpretation projects added yet for this month.",
                  noResultsOverlayLabel: "No matches for your filters",
                }}
                pageSizeOptions={[20, 50, 100]}
                getRowId={(row) => row.id}
                rows={columnFilteredData || []}
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange}
                disableColumnSorting
                onStateChange={(e) => {
                  const lookup = e.filter?.filteredRowsLookup;
                  if (!lookup) return;

                  const nextIds = Object.keys(lookup).filter(
                    (key) => lookup[key],
                  );
                  nextIds.sort();

                  const prevIds = prevFilteredRowIdsRef.current;
                  const isSame =
                    prevIds.length === nextIds.length &&
                    prevIds.every((id, idx) => id === nextIds[idx]);

                  if (!isSame) {
                    prevFilteredRowIdsRef.current = nextIds;
                    setFilterRowIds(nextIds);
                  }
                }}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                apiRef={apiRef}
                density={density}
                disableRowSelectionOnClick={!!openFilter}
                onRowClick={(params, event) => {
                  if (openFilter) {
                    setOpenFilter(null);
                    return;
                  }

                  onOpenAssignment?.(params.id);
                }}
                getCellClassName={(params) => {
                  return params.value === "No" ||
                    params.value === "N/A" ||
                    params.value === "0%" ||
                    params.value === "null"
                    ? "notAvailable"
                    : "";
                }}
                slots={{ toolbar: CustomToolbar, noRowsOverlay: NoRowsOverlay }}
                columns={filteredColumns}
                slotProps={{
                  row: {
                    style: { cursor: "context-menu" },
                  },
                  toolbar: {
                    hasAnyActiveFilters,
                    onClear: () => clearAllFiltersRef.current(),
                    theme,
                    currentTab: assignmentTab,
                    onTabChange: onAssignmentTabChange,
                    needsFinalizationCount,

                    showQuickFilter: true,
                    printOptions: { disableToolbarButton: true },
                    csvOptions: { disableToolbarButton: true },
                  },
                  loadingOverlay: {
                    variant: "skeleton",
                    noRowsVariant: "skeleton",
                  },
                }}
                ignoreDiacritics
                sx={{
                  backgroundColor: theme.palette.background.default,
                  borderRadius: "12px",
                  height: "100%",
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: "bold",
                    color: theme.palette.primary.main,
                  },
                  "& .MuiDataGrid-scrollbarFiller": {
                    backgroundColor: theme.palette.background.alt,
                  },
                  "& .MuiDataGrid-sortIcon": {
                    display: "none !important",
                  },
                  "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
                    display: "none !important",
                  },
                  "& .MuiDataGrid-menuIconButton": {
                    display: "none !important",
                  },
                  "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
                    display: "none !important",
                  },
                  "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                    flex: 1,
                  },
                  "& .MuiDataGrid-columnSeparator": {
                    color: theme.palette.primary.main,
                  },
                  "& .MuiButton-root": {
                    color: theme.palette.primary.main,
                  },
                  "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                    outline: "none !important",
                  },
                  "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus": {
                    outline: "none !important",
                  },
                }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

const LinguistDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [assignments, setAssignments] = useState([]);
  const [offers, setOffers] = useState([]);
  const [hourlyRate, setHourlyRate] = useState(0);
  const currentYear = dayjs().year();
  const currentMonth = dayjs().format("MMM");
  const [selectedYears] = useState([currentYear]);
  const [selectedMonths] = useState([currentMonth]);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [assignmentTab, setAssignmentTab] = useState("all");
  const handleAssignmentTabChange = useCallback((event, newValue) => {
    setAssignmentTab(newValue);
  }, []);
  const authRetryCountRef = useRef(0);

  const isInterpretationService = (item) => {
    if (String(item?.ServiceCategory || "").toLowerCase() === "interpretation") {
      return true;
    }

    return Boolean(item?.InterpretationStatus);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    setActionMessage("");

    try {
      const [assignmentsResponse, offersResponse, rateResponse] = await Promise.all([
        linguistApi.get("/me/assignments", {
          params: { includePastConfirmed: true },
        }),
        linguistApi.get("/me/offers"),
        linguistApi.get("/me/rate"),
      ]);

      setAssignments(assignmentsResponse.data || []);
      setOffers(offersResponse.data || []);
      setHourlyRate(rateResponse.data?.HourlyRate || 0);
      authRetryCountRef.current = 0;
    } catch (requestError) {
      const code = String(requestError?.code || "");
      if (
        code === "LINGUIST_AUTH_USER_UNAVAILABLE" ||
        code === "LINGUIST_AUTH_TOKEN_UNAVAILABLE"
      ) {
        const currentUser = auth.currentUser || (await waitForLinguistAuthUser(2500));
        if (!currentUser) {
          setError(
            "Your session could not be verified right now. Please sign in again if this continues.",
          );
          return;
        }

        if (authRetryCountRef.current < 2) {
          authRetryCountRef.current += 1;
          setTimeout(() => {
            loadData();
          }, 700);
          return;
        }

        setError("We are still finalizing your secure session. Please refresh this page once.");
        return;
      }

      const status = Number(requestError?.response?.status || 0);
      if (status === 401 || status === 403) {
        const currentUser = auth.currentUser || (await waitForLinguistAuthUser(5000));
        if (!currentUser) {
          setError(
            "Your session could not be verified right now. Please sign in again if this continues.",
          );
          return;
        }

        setError("We could not verify your session right now. Please retry.");
      } else {
        setError(
          requestError?.response?.data?.error ||
            "Unable to load your portal data. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const acceptOffer = useCallback(
    async (offerId) => {
      try {
        setError("");
        setActionMessage("");
        await linguistApi.post(`/offers/${offerId}/accept`);
        setActionMessage("Offer accepted.");
        await loadData();
      } catch (requestError) {
        setError(
          requestError?.response?.data?.error ||
            "This offer is no longer available.",
        );
      }
    },
    [loadData],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const { socket } = useWebSocket();

  useEffect(() => {
    if (!socket) return;

    // Listen for offer status changes (e.g., when your offer is declined because another linguist accepted)
    const handleOfferStatusChanged = (data) => {
      setOffers((prevOffers) =>
        prevOffers.map((offer) =>
          offer.id === data.offerId ? { ...offer, Status: data.newStatus } : offer,
        ),
      );

      // If any of your offers were declined, show a message
      if (data.newStatus === "Declined" && data.declinedReason) {
        setActionMessage(`Your offer for a service was declined because ${data.declinedReason}`);
      }
    };

    socket.on("offer:status_changed", handleOfferStatusChanged);

    return () => {
      socket.off("offer:status_changed", handleOfferStatusChanged);
    };
  }, [socket]);

  const interpretationAssignments = useMemo(
    () => assignments.filter(isInterpretationService),
    [assignments],
  );

  const pendingOfferCount = useMemo(
    () => offers.filter((offer) => String(offer?.Status || "").trim() === "Offered").length,
    [offers],
  );

  if (loading) {
    return (
      <Box sx={{ py: 8, px: 2 }}>
        <Typography>Loading your portal...</Typography>
      </Box>
    );
  }

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        // 100dvh where supported: bottom controls stay clear of iOS Safari's collapsing chrome
        "@supports (height: 100dvh)": {
          height: `calc(100dvh - ${NAVBAR_HEIGHT + 20}px)`,
        },
      }}
    >
      

      {actionMessage ? (
        <Alert severity="success" sx={{ mb: 1, mx: 1 }}>
          {actionMessage}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 1, mx: 1 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ px: 1, pt: 1, flexShrink: 0 }}>
        <Card
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: "3",
          }}
        >
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="h6" fontWeight={800}>
                  Offer status
                </Typography>
                <Chip
                  size="small"
                  label={`${pendingOfferCount} pending`}
                  color={pendingOfferCount > 0 ? "warning" : "default"}
                />
                {offers.length !== pendingOfferCount ? (
                  <Chip size="small" label={`${offers.length} total`} variant="outlined" />
                ) : null}
              </Box>
              <Typography variant="body2" color="text.secondary">
                Review this assignment's details, then accept the pending offer before another linguist does.
              </Typography>

              <Divider />

              {offers.length > 0 ? (
                <Stack spacing={1.25}>
                  {offers.map((offer) => (
                    <Card
                      key={offer.id}
                      variant="outlined"
                      sx={{ borderColor: theme.palette.divider }}
                    >
                      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Stack
                          spacing={1}
                          direction={{ xs: "column", sm: "row" }}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          justifyContent="space-between"
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {offer?.Service?.Name || `Assignment #${offer?.ServiceID}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatOfferDate(offer?.Service?.Date)}
                              {offer?.Service?.TimeStart
                                ? ` · ${formatOfferTime(offer.Service.TimeStart)}`
                                : ""}
                              {offer?.Service?.TimeEnd
                                ? ` - ${formatOfferTime(offer.Service.TimeEnd)}`
                                : ""}
                            </Typography>
                            <Box
                              sx={{
                                mt: 1,
                                display: "grid",
                                gap: 0.75,
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  sm: "repeat(2, minmax(0, 1fr))",
                                },
                              }}
                            >
                              {[
                                { label: "Date", value: formatOfferDate(offer?.Service?.Date) },
                                { label: "Start", value: formatOfferTime(offer?.Service?.TimeStart) },
                                { label: "End", value: formatOfferTime(offer?.Service?.TimeEnd) },
                                { label: "Service type", value: getOfferServiceType(offer?.Service) },
                                getOfferAddress(offer?.Service)
                                  ? {
                                      label: isInPersonOffer(offer?.Service)
                                        ? "In-person address"
                                        : "Address",
                                      value: getOfferAddress(offer?.Service),
                                      fullWidth: true,
                                    }
                                  : null,
                                getOfferSpecialInstructions(offer?.Service)
                                  ? {
                                      label: "Special instructions",
                                      value: getOfferSpecialInstructions(offer?.Service),
                                      fullWidth: true,
                                    }
                                  : null,
                                { label: "Linguist Pay", value: calculateLinguistPay(offer?.Service?.TimeStart, offer?.Service?.TimeEnd, hourlyRate) },
                              ]
                                .filter(Boolean)
                                .map((detail) => (
                                  <Box
                                    key={detail.label}
                                    sx={{
                                      gridColumn: detail.fullWidth ? "1 / -1" : "auto",
                                    }}
                                  >
                                    <Typography variant="caption" color="text.secondary">
                                      {detail.label}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {detail.value}
                                    </Typography>
                                  </Box>
                                ))}
                            </Box>
                          </Box>

                          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                            <Chip
                              size="small"
                              label={getOfferDisplayStatus(offer?.Status)}
                              color={getOfferStatusColor(theme, offer?.Status)}
                            />
                            {String(offer?.Status || "").trim() === "Offered" ? (
                              <Button
                                variant="contained"
                                onClick={() => acceptOffer(offer.id)}
                              >
                                Accept
                              </Button>
                            ) : null}
                            <Button
                              variant="text"
                              onClick={() => navigate(`/linguist-portal/assignments/${offer.ServiceID}`)}
                            >
                              Details
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No assignment offers found right now.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Box
        bgcolor={theme.palette.background.alt}
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          marginTop: "0.5rem",
          minHeight: 0,
        }}
      >
        <InterpretationAssignmentsGrid
          assignments={interpretationAssignments}
          selectedYear={selectedYears?.[0] || currentYear}
          selectedMonths={selectedMonths}
          onOpenAssignment={(assignmentId) =>
            navigate(`/linguist-portal/assignments/${assignmentId}`)
          }
          assignmentTab={assignmentTab}
          onAssignmentTabChange={handleAssignmentTabChange}
        />
      </Box>
    </Box>
  );
};

export default LinguistDashboard;